import type { ObjectId } from 'mongodb';

import { ProgramType } from './ProgramTypes';

export enum BatteryType {
  backupBattery = 'backupBattery',
  arbitrageBattery = 'arbitrageBattery',
}

export const BatteryTypeLabels: Record<BatteryType, string> = {
  [BatteryType.backupBattery]: 'Backup Battery',
  [BatteryType.arbitrageBattery]: 'Arbitrage Battery',
};

export enum AdderType {
  arbitrageBattery = 'arbitrageBattery',
  arbitrageBatteryCost = 'arbitrageBatteryCost',
  electricalUpgrade = 'electricalUpgrade',
  backupBattery = 'backupBattery',
  backupBatteryKwh = 'backupBatteryKwh',
  backupBatteryPriceCap = 'backupBatteryPriceCap',
  batteryMinimumCapacity = 'batteryMinimumCapacity',
  solarReadiness = 'solarReadiness',
  arbitrageBatteryPriceCap = 'arbitrageBatteryPriceCap',
}

export enum AdditionalCostAdderType {
  backupBattery = 'backupBattery',
  arbitrageBattery = 'arbitrageBattery',
  solarReadiness = 'solarReadiness',
}

export interface AvailableAdders {
  arbitrageBattery?: number;
  arbitrageBatteryCost?: number;
  backupBattery?: number;
  backupBatteryKwh?: number;
  electricalUpgrade?: number;
  solarReadiness?: number;
}

type AdderMap = {
  [key in ProgramType]: {
    [adder in AdderType]?: string;
  };
};

export const adderMap: AdderMap = {
  [ProgramType.solar]: {
    [AdderType.electricalUpgrade]: 'Electrical Upgrade',
    [AdderType.arbitrageBattery]: 'Arbitrage Battery ($/kWh)',
    [AdderType.arbitrageBatteryCost]: 'Arbitrage Battery (cost)',
    [AdderType.arbitrageBatteryPriceCap]: 'Arbitrage Battery Price Caps',
    [AdderType.backupBatteryKwh]: 'Backup Battery ($/kWh)',
    [AdderType.backupBattery]: 'Backup Battery (cost)',
    [AdderType.backupBatteryPriceCap]: 'Backup Battery Price Cap',
    [AdderType.solarReadiness]: 'Solar Readiness',
    [AdderType.batteryMinimumCapacity]: 'Battery Minimum Capacity (kW)',
  },
  [ProgramType.doePr]: {
    [AdderType.backupBattery]: 'Backup Battery (cost)',
    [AdderType.solarReadiness]: 'Solar Readiness',
  },
  [ProgramType.hvac]: {},
  [ProgramType.waterHeater]: {},
  [ProgramType.generator]: {},
  [ProgramType.newHomes]: {
    [AdderType.arbitrageBattery]: 'Arbitrage Battery ($/kWh)',
    [AdderType.arbitrageBatteryCost]: 'Arbitrage Battery (cost)',
    [AdderType.arbitrageBatteryPriceCap]: 'Arbitrage Battery Price Caps',
    [AdderType.backupBattery]: 'Backup Battery (cost)',
    [AdderType.backupBatteryPriceCap]: 'Backup Battery Price Caps',
  },
  [ProgramType.newCommunity]: {
    [AdderType.arbitrageBattery]: 'Arbitrage Battery ($/kWh)',
    [AdderType.arbitrageBatteryCost]: 'Arbitrage Battery (cost)',
    [AdderType.arbitrageBatteryPriceCap]: 'Arbitrage Battery Price Caps',
    [AdderType.backupBattery]: 'Backup Battery (cost)',
    [AdderType.backupBatteryPriceCap]: 'Backup Battery Price Caps',
  },
  [ProgramType.energyBackup]: {
    [AdderType.arbitrageBatteryPriceCap]: 'Arbitrage Battery Price Caps',
    [AdderType.backupBatteryPriceCap]: 'Backup Battery Price Caps',
  },
  [ProgramType.geothermal]: {},
  [ProgramType.consumerSolar]: {
    [AdderType.arbitrageBattery]: 'Arbitrage Battery ($/kWh)',
  },
} as const;

export interface AdditionalCostAdder {
  type: AdditionalCostAdderType;
  cost: number;
}

export type AdditionalCostAdders = AdditionalCostAdder[];

export type AdderValues = Record<AdderType, AdderDetails[]>;

export interface AdderDetails {
  type: AdderType;
  quantity?: number;
  model?: string;
  manufacturer?: string;
  cost?: number;
  reasonOptionsSelected?: SolarReadinessAdderOption[];
  otherReasonDetails?: string;
}
export interface AccountAdders {
  accountId: ObjectId;
  adders: AdderDetails[];
}

export type AddersDocument = AccountAdders & {
  id: ObjectId;
};

export interface SolarReadinessAdderOption {
  id: number;
  value: string;
  label: string;
}
